import { api, type ApiResponse } from './api'
import { withoutEmpty } from './descarga'
import type {
  AdvancePaymentMethod,
  AdvanceTotals,
  Advisor,
  PriceType,
  QuoteAdvance,
  QuoteAdvanceInput,
} from './quotes'
import type { CustomerMeta } from './customers'

/**
 * Cliente de ventas de tienda.
 *
 * Una venta de tienda es un documento propio, no una cotización con una bandera:
 * no tiene artículos, sets ni cantidades. Su importe es un único valor. Por eso
 * este servicio no reutiliza `quoteApi.save`, aunque comparta la cabecera y los
 * anticipos: mandar `items` aquí sería mandar un campo que el backend no acepta.
 *
 * Las claves de facturación y de pago son las mismas que en cotizaciones para que
 * una venta y una cotización se lean igual en las métricas.
 */

export type StoreSalePriceType = PriceType

/** Los dos únicos estados de una venta: no hay logística. */
export type StoreSaleInvoiceState = 'FACTURADA' | 'PENDIENTE'

/** Un anticipo de tienda tiene la misma forma que el de una cotización. */
export type StoreSaleAdvance = QuoteAdvance

/** Lo que se envía al dar de alta un anticipo. */
export type StoreSaleAdvanceInput = QuoteAdvanceInput

/** Cliente sugerido por el buscador del formulario. */
export interface StoreSaleCustomerSuggestion {
  id: number
  name: string
  document: string | null
  email: string | null
  phone: string | null
  city: string | null
  source: string | null
}

/** Una venta tal y como la devuelve el backend. */
export interface StoreSale {
  id: number
  sale_number: string
  customer_id: number | null
  customer_name: string
  document: string | null
  /**
   * Copia local del contacto con el que se emitió la venta, no una lectura del
   * directorio. Corregir la ficha del cliente después no cambia el documento.
   */
  customer_email: string | null
  customer_phone: string | null
  customer_city: string | null
  customer_source: string | null
  attention_line: string | null
  user_id: number | null
  user_name: string | null
  sale_date: string
  price_type: StoreSalePriceType
  /** Valor único de la venta, sin decimales. */
  amount: string
  /** Alias del backend con el mismo valor, para leerlo junto al de cotización. */
  total: string
  is_invoiced: boolean
  invoice_number: string | null
  advances_total: string
  advances_count: number
  has_advances: boolean
  balance: string
  advances: StoreSaleAdvance[]
  created_at: string | null
  updated_at: string | null
}

/** Lo que se envía al crear o editar. */
export interface StoreSalePayload {
  customer_id?: number | null
  customer_name: string
  document?: string | null
  customer_email?: string | null
  customer_phone?: string | null
  customer_city?: string | null
  /** Lista cerrada del backend: web, referido, redes, visita o `null`. */
  customer_source?: string | null
  attention_line?: string | null
  sale_date?: string | null
  price_type: StoreSalePriceType
  user_id?: number | null
  amount: number
  is_invoiced?: boolean
  invoice_number?: string | null
}

export interface StoreSaleListParams {
  search?: string
  date_from?: string
  date_to?: string
  price_type?: StoreSalePriceType
  is_invoiced?: boolean
  user_id?: number
  page?: number
  per_page?: number
}

export interface StoreSalePage {
  data: StoreSale[]
  meta: CustomerMeta
}

/** Los orígenes admitidos, con las mismas etiquetas que el cotizador. */
export const STORE_SALE_SOURCES: Array<{ label: string; value: string }> = [
  { label: 'Sin origen', value: '' },
  { label: 'Web', value: 'web' },
  { label: 'Referido', value: 'referido' },
  { label: 'Redes sociales', value: 'redes' },
  { label: 'Visita comercial', value: 'visita' },
]

/** Los dos tipos de facturación, con las palabras del comercial. */
export const STORE_SALE_PRICE_TYPES: Array<{ label: string; value: StoreSalePriceType }> = [
  { label: 'Detal', value: 'retail' },
  { label: 'Mayoreo', value: 'wholesale' },
]

/** Etiquetas visibles del estado de facturación. */
export function invoiceStateLabel(sale: Pick<StoreSale, 'is_invoiced'>): StoreSaleInvoiceState {
  return sale.is_invoiced ? 'FACTURADA' : 'PENDIENTE'
}

/**
 * Normaliza los filtros del listado.
 *
 * Los booleanos van como `1`/`0` y no como `"true"`: Laravel acepta `true`, pero
 * Axios serializa un booleano de JavaScript como la cadena `"true"`, que la regla
 * `boolean` también admite; aun así se manda el número para que la URL sea la
 * misma que usan cotizaciones y el dashboard. Un `''` se descarta porque un
 * filtro vacío no es un filtro.
 */
function listParams(params: StoreSaleListParams): Record<string, unknown> {
  const limpios = withoutEmpty(params) as Record<string, unknown>

  for (const [key, value] of Object.entries(limpios)) {
    if (typeof value === 'boolean') limpios[key] = value ? 1 : 0
  }

  return limpios
}

export const storeSalesApi = {
  list(params: StoreSaleListParams = {}) {
    return api.get<StoreSalePage>('/store-sales', { params: listParams(params) })
  },

  get(id: number) {
    return api.get<ApiResponse<StoreSale>>(`/store-sales/${id}`)
  },

  create(payload: StoreSalePayload) {
    return api.post<ApiResponse<StoreSale>>('/store-sales', payload)
  },

  update(id: number, payload: StoreSalePayload) {
    return api.patch<ApiResponse<StoreSale>>(`/store-sales/${id}`, payload)
  },

  remove(id: number) {
    return api.delete(`/store-sales/${id}`)
  },

  /**
   * Marca la venta como facturada.
   *
   * El número es obligatorio: una factura sin número no se puede auditar. La
   * operación no toca el valor ni los anticipos.
   */
  markInvoiced(id: number, invoiceNumber: string) {
    return api.patch<ApiResponse<StoreSale>>(`/store-sales/${id}/invoice`, {
      invoice_number: invoiceNumber,
    })
  },

  advisors() {
    return api.get<ApiResponse<Advisor[]>>('/store-sales/advisors')
  },

  /** Ventas de un cliente, para la sección «Ventas tienda» de su ficha. */
  customer(customerId: number, params: { page?: number; per_page?: number } = {}) {
    return api.get<StoreSalePage>(`/customers/${customerId}/store-sales`, {
      params: listParams(params),
    })
  },

  listAdvances(id: number) {
    return api.get<ApiResponse<StoreSaleAdvance[]>>(`/store-sales/${id}/advances`)
  },

  createAdvance(id: number, payload: StoreSaleAdvanceInput) {
    return api.post<ApiResponse<AdvanceTotals & { advance: StoreSaleAdvance }>>(
      `/store-sales/${id}/advances`,
      payload,
    )
  },

  deleteAdvance(id: number, advanceId: number) {
    return api.delete<ApiResponse<AdvanceTotals>>(`/store-sales/${id}/advances/${advanceId}`)
  },
}

export type { AdvancePaymentMethod, Advisor, CustomerMeta }
